<template>
    <div class="horizontal-bar-chart-container">
        <div
            v-for="(item, index) in sortedData"
            :key="index"
            class="ppv2-flex ppv2-flex-row ppv2-justify-center"
            :style="{ gap: normalizeSize(15, true) }"
        >
            <div class="bar-label-wrapper">
                <span class="bar-label poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }" :title="item.label">{{
                    truncateLabel(item.label)
                }}</span>
                <div class="bar-tooltip">
                    <div class="tooltip-name poppinsMedium" :style="{ fontSize: normalizeFontSize(13) }">{{ item.label }}</div>
                    <div class="tooltip-amount" :style="{ fontSize: normalizeFontSize(16) }">
                        {{ formatAmount(item.valueN) }}{{ $symbolCurrency }}
                    </div>
                </div>
            </div>
            <div class="bar-container">
                <div class="bar-bg">
                    <div class="bar-fill" :class="getTypeClass(item.type)" :style="{ width: item.percent + '%' }"></div>
                </div>
            </div>
            <span class="bar-percent" :style="{ fontSize: normalizeFontSize(18) }">{{ formatPercent(item.percent) }}</span>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'HorizontalBarChart',
    props: {
        data: {
            type: Array,
            required: true,
            // Expected format: [{ label: 'Item', valueN: 100, valueN1: 80, percent: 48.3 }]
        },
        showRank: {
            type: Boolean,
            default: true,
        },
        maxValue: {
            type: Number,
            default: null,
        },
    },
    computed: {
        sortedData() {
            return [...this.data].slice(0, 10) // Limit to top 10
        },
        calculatedMax() {
            if (this.maxValue) return this.maxValue
            return Math.max(...this.data.map((item) => Math.max(item.valueN || 0, item.valueN1 || 0)))
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getBarWidth(value) {
            if (!value || !this.calculatedMax) return 0
            return Math.min((value / this.calculatedMax) * 100, 100)
        },
        formatPercent(value) {
            if (typeof value === 'number') {
                return `${value.toFixed(1)}%`
            }
            return value
        },
        truncateLabel(label, maxLength = 20) {
            if (!label) return ''
            if (label.length <= maxLength) return label
            return label.substring(0, maxLength) + '...'
        },
        formatAmount(value) {
            if (typeof value === 'number') {
                return value.toLocaleString(this.$currencyLocale?.replace('_', '-') || 'fr-FR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })
            }
            return value
        },
        getTypeClass(type) {
            const typeClasses = {
                prestation: 'type-prestation',
                product: 'type-product',
                voucher: 'type-voucher',
                package: 'type-package',
                food: 'type-food',
                foodpack: 'type-foodpack',
                other: 'type-other',
            }
            return typeClasses[type] || 'type-other'
        },
    },
}
</script>

<style scoped>
.horizontal-bar-chart-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
}

.type-prestation {
    background: linear-gradient(90deg, #4a3aff 0%, #6d3aff 100%);
}

.type-product {
    background: linear-gradient(90deg, #00d589 0%, #00a86b 100%);
}

.type-voucher {
    background: linear-gradient(90deg, #ffb347 0%, #ff8c00 100%);
}

.type-package {
    background: linear-gradient(90deg, #c893fd 0%, #d84895 100%);
}

.type-food {
    background: linear-gradient(90deg, #f97316 0%, #ea580c 100%);
}

.type-foodpack {
    background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.type-other {
    background: linear-gradient(90deg, #6b7280 0%, #4b5563 100%);
}

.bar-label-wrapper {
    position: relative;
    min-width: 20%;
    max-width: 180px;
}

.bar-label {
    color: #fff;
    line-height: 20px;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
}

.bar-tooltip {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 5px;
    background: #1e1b39;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 10px 14px;
    z-index: 100;
    min-width: 200px;
    opacity: 0;
    visibility: hidden;
    transition:
        opacity 0.2s,
        visibility 0.2s;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.bar-label-wrapper:hover .bar-tooltip {
    opacity: 1;
    visibility: visible;
}

.tooltip-name {
    color: #fff;
    margin-bottom: 4px;
    word-wrap: break-word;
}

.tooltip-amount {
    color: #00d589;
    font-family: Inter, sans-serif;
    font-weight: 600;
}

.bar-container {
    flex: 1;
    min-width: 25vh;
}

.bar-bg {
    width: 100%;
    height: 19px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    position: relative;
    overflow: hidden;
}

.bar-fill {
    height: 100%;
    border-radius: 8px;
    transition: width 0.5s ease;
}

.bar-percent {
    color: #fff;
    font-family: Inter, sans-serif;
    font-weight: 500;
    line-height: 20px;
    min-width: 60px;
    text-align: right;
}
</style>
